/**
 * Routes (P2 §3.1): deep links for every entity; each feature owns its sub-routes (`<Routes>` inside its page) and is a
 * lazy chunk. Filters/tabs live in URL search params.
 */
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet, type RouteObject } from 'react-router';
import { SessionRoot } from '@app/core/auth';
import { Workspace } from '@app/shell/Workspace';
import { Login, ResetPassword, AcceptInvite } from '@app/shell/Login';
import { ErrorBoundary } from '@app/shell/ErrorBoundary';
import { Fatal, Loading } from '@app/shell/Fatal';

const Today = lazy(() => import('@app/features/today'));
const Leads = lazy(() => import('@app/features/leads'));
const Calls = lazy(() => import('@app/features/calls'));
const Jobs = lazy(() => import('@app/features/jobs'));
const Dispatch = lazy(() => import('@app/features/dispatch'));
const Techs = lazy(() => import('@app/features/techs'));
const Customers = lazy(() => import('@app/features/customers'));
const Payments = lazy(() => import('@app/features/payments'));
const Passport = lazy(() => import('@app/features/passport'));
const Parts = lazy(() => import('@app/features/parts'));
const Analytics = lazy(() => import('@app/features/analytics'));
const Fleet = lazy(() => import('@app/features/fleet'));
const Settings = lazy(() => import('@app/features/settings'));
const Onboarding = lazy(() => import('@app/features/onboarding'));
const AccountSecurity = lazy(() => import('@app/features/account'));
// The second sign-in step, between the password and the app: outside the tenant SessionRoot, like the sign-in page.
const SecondStep = lazy(() => import('@app/shell/SecondStep'));
// Ride N Repair's own GTM desk (platform principal only; its own gate and shell, outside the tenant SessionRoot).
const Outreach = lazy(() => import('@app/features/outreach'));

const eb = (el: React.ReactNode) => <ErrorBoundary>{el}</ErrorBoundary>;

/** The workshop app's routes, under its shell (shell/Workspace.tsx renders them for every store but a dealer's). */
const WORKSHOP: RouteObject[] = [
  { index: true, element: <Navigate to="/today" replace /> },
  { path: 'today', element: eb(<Today />) },
  { path: 'leads/*', element: eb(<Leads />) },
  { path: 'calls/*', element: eb(<Calls />) },
  { path: 'jobs/*', element: eb(<Jobs />) },
  { path: 'dispatch/*', element: eb(<Dispatch />) },
  { path: 'techs/*', element: eb(<Techs />) },
  { path: 'customers/*', element: eb(<Customers />) },
  { path: 'payments/*', element: eb(<Payments />) },
  { path: 'passport/*', element: eb(<Passport />) },
  { path: 'parts/*', element: eb(<Parts />) },
  { path: 'analytics/*', element: eb(<Analytics />) },
  { path: 'fleet/*', element: eb(<Fleet />) },
  { path: 'settings/*', element: eb(<Settings />) },
  { path: 'onboarding', element: eb(<Onboarding />) },
  { path: 'account/security', element: eb(<AccountSecurity />) },
  { path: '*', element: <Navigate to="/today" replace /> },
];

export const router = createBrowserRouter([
  { path: '/login', element: <Login /> },
  { path: '/login/step', element: <Suspense fallback={<Loading />}><ErrorBoundary><SecondStep /></ErrorBoundary></Suspense> },
  { path: '/reset', element: <ResetPassword /> },
  { path: '/invite/:token', element: <AcceptInvite /> },
  { path: '/outreach/*', element: <Suspense fallback={<Loading />}><ErrorBoundary><Outreach /></ErrorBoundary></Suspense> },
  {
    path: '/',
    element: <SessionRoot loading={<Loading />} failed={(e, retry) => <Fatal error={e} retry={retry} />}><Outlet /></SessionRoot>,
    children: [{
      // The workshop shell and its routes, or (a dealership with the dealer workspace on) the dealer workspace instead.
      // A splat route, so either app's own <Routes> sit under a parent that matches every path below it.
      path: '*',
      element: <Workspace workshop={WORKSHOP} />,
    }],
  },
]);
